// Settings → Integrations.

import { useEffect, useRef, useState } from "react";
import {
  commitCsvImport, decideMerge, fetchImportBatches, getMergeSuggestions,
  importDocuments, importMailbox, previewCsvImport, undoImportBatch,
  type MergeSuggestion,
} from "../api";
import type { CsvPreview, ImportBatchRow } from "../types";
import { useAppData } from "../store";
import { Avatar, Btn } from "../components/Primitives";
import { Ic } from "../components/Icon";

/** Graph review — suspected duplicate people/companies, one click each. */
function GraphReviewCard() {
  const [rows, setRows] = useState<MergeSuggestion[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    getMergeSuggestions().then(setRows).catch(() => setRows([]));
  }, []);

  const decide = async (id: string, action: "merge" | "reject") => {
    setBusy(id);
    try {
      await decideMerge(id, action);
      setRows((r) => (r ?? []).filter((x) => x.id !== id));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={Card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Ic name="route" size={16} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>Graph review</span>
        {rows !== null && rows.length > 0 && (
          <span className="pill review" style={{ fontSize: 10.5 }}>
            <span className="dot" />{rows.length} to review
          </span>
        )}
      </div>
      <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 6 }}>
        Possible duplicates in the firm's knowledge graph — same founder under
        two identities, same company under two names. Merging is reversible.
      </div>
      {rows === null ? (
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 12 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 12 }}>
          Nothing to review — the graph is clean.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {rows.map((s) => (
            <div key={s.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
              border: "1px solid var(--border)", borderRadius: 6, fontSize: 12.5,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {s.primary.name}
                  <span style={{ color: "var(--ink-4)", margin: "0 6px" }}>≟</span>
                  {s.duplicate.name}
                  <span style={{ fontSize: 10.5, color: "var(--ink-3)", marginLeft: 8,
                                 textTransform: "capitalize" }}>{s.kind}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                  {s.reason} · {s.primary.detail} vs {s.duplicate.detail}
                </div>
              </div>
              <Btn size="sm" onClick={() => decide(s.id, "merge")}
                   disabled={busy === s.id}>Same — merge</Btn>
              <Btn size="sm" kind="ghost" onClick={() => decide(s.id, "reject")}
                   disabled={busy === s.id}>Different</Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Firm-memory import (KG-4/5): CSV backbone · bulk documents · mailbox ──

const CSV_FIELDS = [
  "company", "founders", "sector", "stage", "status", "date", "roundSize",
  "valuation", "geography", "owner", "email", "domain", "notes", "exit",
  "ignore",
];

const KIND_LABEL: Record<ImportBatchRow["kind"], string> = {
  csv: "CSV", docs: "Documents", mailbox: "Mailbox",
};

function batchSummary(b: ImportBatchRow): string {
  if (b.kind === "csv") {
    return `${b.created} deals created${b.skipped ? ` · ${b.skipped} skipped` : ""}`;
  }
  if (b.kind === "docs") {
    return `${b.attached} files attached${b.unmatched ? ` · ${b.unmatched} unmatched` : ""}`;
  }
  return `${b.emails} emails imported`;
}

function ImportCard() {
  const { refresh } = useAppData();
  const [batches, setBatches] = useState<ImportBatchRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  // CSV two-step: preview (mapping review) → commit.
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [defaultStatus, setDefaultStatus] = useState("passed");

  const csvInput = useRef<HTMLInputElement>(null);
  const docsInput = useRef<HTMLInputElement>(null);
  const mboxInput = useRef<HTMLInputElement>(null);

  const loadBatches = () =>
    fetchImportBatches().then(setBatches).catch(() => {
      setBatches([]);
      setError("Couldn't load the import history — reload to see past runs and Undo.");
    });
  useEffect(() => { void loadBatches(); }, []);

  const run = async (kind: string, fn: () => Promise<string>) => {
    if (busy) return;
    setBusy(kind); setError(null); setResult(null);
    try {
      setResult(await fn());
      await refresh();
      await loadBatches();
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  const pickCsv = async (file: File) => {
    if (busy) return;
    setBusy("preview"); setError(null); setResult(null); setPreview(null);
    try {
      setCsvFile(file);
      setPreview(await previewCsvImport(file));
    } catch (e) {
      setCsvFile(null);
      setError(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  const setField = (column: string, field: string) => {
    if (!preview) return;
    setPreview({
      ...preview,
      mapping: preview.mapping.map((m) => (m.column === column ? { ...m, field } : m)),
    });
  };

  const commitCsv = () =>
    run("csv", async () => {
      const r = await commitCsvImport(csvFile!, preview!.mapping, defaultStatus);
      setCsvFile(null); setPreview(null);
      return `Created ${r.created} deals${r.skipped.length ? ` · ${r.skipped.length} rows skipped` : ""}.`;
    });

  const undo = async (id: string) => {
    if (busy) return;
    setBusy(`undo-${id}`); setError(null);
    try {
      await undoImportBatch(id);
      await refresh();
      await loadBatches();
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={Card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Ic name="upload" size={16} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>Firm memory import</span>
      </div>
      <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 6 }}>
        Fill Prism's memory from what the firm already has: a pipeline CSV
        becomes archive deals (graph included), a folder of memos attaches to
        the deals they name, a mailbox export becomes durable correspondence —
        with who-introduced-what attribution. Every run can be undone.
      </div>

      <input
        ref={csvInput} type="file" accept=".csv,text/csv" hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void pickCsv(f); }}
      />
      <input
        ref={docsInput} type="file" multiple hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []); e.target.value = "";
          if (!files.length) return;
          void run("docs", async () => {
            const r = await importDocuments(files);
            return `Attached ${r.attached.length} files`
              + (r.conflicts.length ? ` · ${r.conflicts.length} skipped (name already on the deal)` : "")
              + (r.unmatched.length ? ` · unmatched: ${r.unmatched.join(", ")}` : ".");
          });
        }}
      />
      <input
        ref={mboxInput} type="file" accept=".mbox,application/mbox" hidden
        onChange={(e) => {
          const f = e.target.files?.[0]; e.target.value = "";
          if (!f) return;
          void run("mailbox", async () => {
            const r = await importMailbox(f);
            const deals = Object.keys(r.matched).length;
            return `Imported ${r.emails} emails across ${deals} deals`
              + (r.referrers.length ? ` · ${r.referrers.length} introducers found.` : ".");
          });
        }}
      />

      {!preview && (
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <Btn size="sm" ico="file" disabled={!!busy} onClick={() => csvInput.current?.click()}>
            {busy === "preview" ? "Reading CSV…" : "Import pipeline CSV"}
          </Btn>
          <Btn size="sm" ico="folder" disabled={!!busy} onClick={() => docsInput.current?.click()}>
            {busy === "docs" ? "Attaching…" : "Drop documents"}
          </Btn>
          <Btn size="sm" ico="mail" disabled={!!busy} onClick={() => mboxInput.current?.click()}>
            {busy === "mailbox" ? "Importing…" : "Import mailbox (.mbox)"}
          </Btn>
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: "var(--neg)", marginTop: 10 }}>{error}</div>}
      {result && <div style={{ fontSize: 12, color: "var(--pos)", fontWeight: 500, marginTop: 10 }}>{result}</div>}

      {preview && csvFile && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>
            {csvFile.name} · {preview.rows} rows
            {preview.truncated && (
              <span style={{ color: "var(--warn)", fontWeight: 600, marginLeft: 8 }}>
                — file is larger; only the first {preview.rows} rows import
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--ink-3)", margin: "3px 0 10px" }}>
            Check the column mapping before importing — Prism guessed it from
            the headers and sample values.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {preview.mapping.map((m) => (
              <div key={m.column} style={{
                display: "grid", gridTemplateColumns: "1fr 130px 1.2fr",
                gap: 10, alignItems: "center", padding: "6px 10px",
                border: "1px solid var(--border)", borderRadius: 6,
              }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.column}
                </span>
                <select
                  className="input" value={m.field}
                  onChange={(e) => setField(m.column, e.target.value)}
                  style={{ fontSize: 12 }}
                >
                  {CSV_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <span style={{ fontSize: 11, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.sample || "—"}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            <span style={{ fontSize: 12, color: "var(--ink-2)" }}>Rows without a status land as</span>
            <select
              className="input" value={defaultStatus}
              onChange={(e) => setDefaultStatus(e.target.value)}
              style={{ fontSize: 12, width: 110 }}
            >
              <option value="passed">passed</option>
              <option value="review">review</option>
              <option value="new">new</option>
            </select>
            <span style={{ flex: 1 }} />
            <Btn size="sm" kind="accent" disabled={!!busy} onClick={commitCsv}>
              {busy === "csv" ? "Importing…" : `Import ${preview.rows} rows`}
            </Btn>
            <Btn size="sm" kind="ghost" disabled={!!busy}
                 onClick={() => { setCsvFile(null); setPreview(null); }}>
              Cancel
            </Btn>
          </div>
        </div>
      )}

      {batches !== null && batches.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{
            fontSize: 10, fontWeight: 600, textTransform: "uppercase",
            letterSpacing: "0.08em", color: "var(--ink-3)", marginBottom: 7,
          }}>
            Import history
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {batches.map((b) => (
              <div key={b.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "7px 12px",
                border: "1px solid var(--border)", borderRadius: 6, fontSize: 12.5,
                opacity: b.status === "undone" ? 0.55 : 1,
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.05em", color: "var(--ink-3)",
                  background: "var(--raised)", padding: "1px 7px", borderRadius: 8,
                }}>
                  {KIND_LABEL[b.kind]}
                </span>
                <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {b.filename}
                </span>
                <span style={{ fontSize: 11.5, color: "var(--ink-3)", flex: 1, minWidth: 0 }}>
                  {batchSummary(b)}
                </span>
                <Avatar id={b.by} size="sm" />
                {b.status === "active" ? (
                  <Btn size="sm" kind="ghost" disabled={!!busy}
                       onClick={() => undo(b.id)}>
                    {busy === `undo-${b.id}` ? "Undoing…" : "Undo"}
                  </Btn>
                ) : (
                  <span style={{ fontSize: 11, color: "var(--ink-4)" }}>Undone</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InDevBadge() {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 600,
      letterSpacing: "0.02em", color: "var(--ink-3)", background: "var(--raised)",
      border: "1px solid var(--border)",
    }}>In development</span>
  );
}

export function IntegrationsScreen() {
  const { data } = useAppData();
  const [copied, setCopied] = useState(false);
  const inboxAddr = data?.firm.inboundEmail ?? "inbound email not configured";
  const copy = async () => {
    await navigator.clipboard.writeText(inboxAddr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className="page">
      <div className="topbar">
        <span className="crumb">Settings</span>
        <span className="crumb-sep">/</span>
        <h1>Integrations</h1>
      </div>
      <div className="scroll" style={{ padding: "22px 28px 60px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 760 }}>
          <ImportCard />
          <GraphReviewCard />
          <div style={Card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Ic name="mail" size={16} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Firm forwarding address</span>
              <span className="pill invested" style={{ fontSize: 10.5 }}>
                <span className="dot" />Active
              </span>
            </div>
            <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 6 }}>
              Forward any inbound email here. Extraction starts within seconds.
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              marginTop: 14, padding: "10px 14px",
              background: "var(--bg)", borderRadius: 6,
            }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 13, flex: 1 }}>
                {inboxAddr}
              </span>
              <Btn size="sm" ico={copied ? "check" : "file"} onClick={copy}>
                {copied ? "Copied" : "Copy"}
              </Btn>
            </div>
          </div>

          <div style={{ ...Card, opacity: 0.65 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Ic name="plug" size={16} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Outlook plugin</span>
              <InDevBadge />
            </div>
            <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 6 }}>
              Send any open email to Prism with one click. Available in Microsoft 365.
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <Btn size="sm" ico="ext" disabled>Manage admin install</Btn>
              <Btn size="sm" kind="ghost" disabled>Send install link</Btn>
            </div>
          </div>

          <div style={{ ...Card, opacity: 0.65 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Ic name="cog" size={16} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>API keys</span>
              <InDevBadge />
              <span style={{ flex: 1 }} />
              <Btn size="sm" ico="plus" disabled>Create key</Btn>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
              {[
                { k: "prsk_live_…7a2c", purpose: "Pipeline ETL", who: "LS" },
                { k: "prsk_live_…91f3", purpose: "Custom Zapier", who: "MS" },
              ].map((k) => (
                <div key={k.k} style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr auto 70px",
                  gap: 12, padding: "9px 12px",
                  border: "1px solid var(--border)", borderRadius: 6,
                  alignItems: "center", fontSize: 13,
                }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{k.k}</span>
                  <span style={{ color: "var(--ink-2)" }}>{k.purpose}</span>
                  <Avatar id={k.who} size="sm" />
                  <span style={{
                    color: "var(--ink-4)", fontSize: 12, textAlign: "right",
                  }}>Revoke</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Card: React.CSSProperties = {
  padding: 18, border: "1px solid var(--border)",
  borderRadius: 8, background: "var(--surface)",
};
